import React, { useState } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TeamPerformanceOverview from './components/TeamPerformanceOverview';
import TeamManagementTab from './components/TeamManagementTab';
import BudgetTrackingTab from './components/BudgetTrackingTab';
import OperationalMetricsTab from './components/OperationalMetricsTab';
import NotificationPanel from './components/NotificationPanel';

const DepartmentManagerHub = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showNotifications, setShowNotifications] = useState(false);

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      icon: 'BarChart3',
      description: 'Team performance and activity summary'
    },
    {
      id: 'team',
      label: 'Team Management',
      icon: 'Users',
      description: 'Employee management and coordination'
    },
    {
      id: 'budget',
      label: 'Budget Tracking',
      icon: 'DollarSign',
      description: 'Financial oversight and approvals'
    },
    {
      id: 'metrics',
      label: 'Operational Metrics',
      icon: 'TrendingUp',
      description: 'Performance analytics and KPIs'
    }
  ];

  const quickActions = [
    {
      id: 1,
      label: 'Schedule Team Meeting',
      icon: 'Calendar',
      action: () => console.log('Schedule meeting'),
      color: 'bg-primary text-primary-foreground'
    },
    {
      id: 2,
      label: 'Generate Report',
      icon: 'FileText',
      action: () => console.log('Generate report'),
      color: 'bg-success text-success-foreground'
    },
    {
      id: 3,
      label: 'Review Expenses',
      icon: 'Receipt',
      action: () => console.log('Review expenses'),
      color: 'bg-warning text-warning-foreground'
    },
    {
      id: 4,
      label: 'Assign Tasks',
      icon: 'UserPlus',
      action: () => console.log('Assign tasks'),
      color: 'bg-accent text-accent-foreground'
    }
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <TeamPerformanceOverview />;
      case 'team':
        return <TeamManagementTab />;
      case 'budget':
        return <BudgetTrackingTab />;
      case 'metrics':
        return <OperationalMetricsTab />;
      default:
        return <TeamPerformanceOverview />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <div className={`transition-smooth ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <Header sidebarCollapsed={sidebarCollapsed} />
        
        <main className="pt-16">
          <div className="p-6">
            <Breadcrumb />
            
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
              <div className="mb-4 lg:mb-0">
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Department Manager Hub
                </h1>
                <p className="text-muted-foreground">
                  Operational oversight and team coordination dashboard for middle management
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={toggleNotifications}
                  iconName="Bell"
                  iconPosition="left"
                >
                  Notifications
                </Button>
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                >
                  New Task
                </Button>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {quickActions?.map((action) => (
                <button
                  key={action?.id}
                  onClick={action?.action}
                  className={`${action?.color} p-4 rounded-lg transition-smooth hover:opacity-90 text-left`}
                >
                  <Icon name={action?.icon} size={24} className="mb-2" />
                  <p className="text-sm font-medium">{action?.label}</p>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
              {/* Main Content Area */}
              <div className="xl:col-span-3">
                {/* Tab Navigation */}
                <div className="bg-card border border-border rounded-lg mb-6 card-shadow">
                  <div className="border-b border-border">
                    <nav className="flex space-x-1 p-1">
                      {tabs?.map((tab) => (
                        <button
                          key={tab?.id}
                          onClick={() => handleTabChange(tab?.id)}
                          className={`flex items-center space-x-2 px-4 py-3 rounded-md text-sm font-medium transition-smooth ${
                            activeTab === tab?.id
                              ? 'bg-primary text-primary-foreground'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }`}
                        >
                          <Icon name={tab?.icon} size={16} />
                          <span className="hidden sm:inline">{tab?.label}</span>
                        </button>
                      ))}
                    </nav>
                  </div>
                  
                  {/* Tab Description */}
                  <div className="p-4 bg-muted/50">
                    <p className="text-sm text-muted-foreground">
                      {tabs?.find(tab => tab?.id === activeTab)?.description}
                    </p>
                  </div>
                </div>

                {/* Tab Content */}
                <div className="space-y-6">
                  {renderTabContent()}
                </div>
              </div>

              {/* Notifications Panel */}
              <div className="xl:col-span-1">
                <div className={`transition-smooth ${showNotifications ? 'block' : 'hidden xl:block'}`}>
                  <NotificationPanel />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DepartmentManagerHub;